import React, { useEffect, useState, useCallback, useRef } from "react";
import PropTypes from 'prop-types';
import { translate } from 'react-i18next';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Modal from "component/Modal";
import DateIntervalInput from 'component/DateIntervalInput/NewDateIntevalInput'
import TextField from "component/TextField";
import RegionBranchMultipleSelect from 'component/branch/RegionBranchMultipleSelectNew'
import ThemeSelectField from 'component/priceBanner/selects/ThemeSelectField';
import FileInput from 'component/FileInput';
import SupplyItemsNotFoundBox from 'component/ircListsSupply/SupplyItemsNotFoundBox';
import 'container/priceBanner/styles.css';
import moment from "moment";
import * as pricebannerListActions from 'action/pricebannerList';
import {
  PRICEBANNER_INVALID_STORES_I18N_KEY,
  CMPM_PRODUCT_EXTRAS_STORAGE_KEY,
  CLUBE_LEROY_PRODUCT_EXTRAS_STORAGE_KEY,
  countCsvProductDuplicatesRemoved,
  getProductExtrasStorageKeyByTheme,
  isClubeLeroyThemeCode,
  isCmpmThemeCode,
} from 'utils/priceBannerCsvWithStores';

function normalizeCsvCell(cell) {
  return String(cell || '')
    .trim()
    .replace(/^\ufeff/, '')
    .replace(/^"|"$/g, '');
}

function normalizeCsvHeader(cell) {
  return normalizeCsvCell(cell).toLowerCase();
}

function isStoreColumnHeader(header) {
  return (
    header === 'lojas' ||
    header === 'loja' ||
    header === 'stores' ||
    header === 'store' ||
    header.includes('loja')
  );
}

function detectCsvSeparator(line) {
  const counts = [
    [';', (line.match(/;/g) || []).length],
    [',', (line.match(/,/g) || []).length],
    ['\t', (line.match(/\t/g) || []).length],
  ];
  const best = counts.sort((a, b) => b[1] - a[1])[0];
  if (best[1] > 0) return best[0];
  return line.includes(';') ? ';' : ',';
}

function splitCsvLine(line, separator) {
  const cols = line.split(separator).map(normalizeCsvCell);
  if (cols.length >= 2) return cols;
  for (const sep of [';', ',', '\t']) {
    if (sep !== separator) {
      const alt = line.split(sep).map(normalizeCsvCell);
      if (alt.length >= 2) return alt;
    }
  }
  return cols;
}

function detectClubeLeroySeparator(line, storesInCsv, fallbackSeparator) {
  if (storesInCsv) {
    for (const sep of [';', '\t', ',']) {
      const cols = line.split(sep).map(c => c.trim());
      if (cols.length === 3 || cols.length === 4) return sep;
    }
  }

  for (const sep of [';', '\t', ',']) {
    const cols = line.split(sep).map(c => c.trim());
    if (cols.length === 2) return sep;
    if (
      cols.length === 3 &&
      /^\d{8}$/.test(cols[0]) &&
      /^\d+$/.test(cols[1]) &&
      /^\d+$/.test(cols[2])
    ) {
      return sep;
    }
    if (cols.length === 1 && sep === ',') {
      const inner = line.split(';').map(c => c.trim());
      if (inner.length === 2) return ';';
    }
  }
  return fallbackSeparator;
}

function parseClubeLeroyLine(line, sep, storesInCsv) {
  const columns = line.split(sep).map(col => col.trim());

  if (columns.length === 2) {
    if (storesInCsv) return null;
    return { lm: columns[0], discountPercent: columns[1] };
  }

  if (columns.length === 3) {
    const lm = columns[0];
    const second = columns[1];
    const third = columns[2];

    if (storesInCsv) {
      if (/^\d{1,3}([.,]\d+)?$/.test(second) && third) {
        return { lm, discountPercent: second, store: third };
      }
      return null;
    }

    const isDiscountDecimalSplit =
      /^\d{8}$/.test(lm) &&
      /^\d+$/.test(second) &&
      /^\d+$/.test(third);

    if (isDiscountDecimalSplit) {
      return { lm, discountPercent: `${second},${third}` };
    }
  }

  if (columns.length === 4 && storesInCsv) {
    const lm = columns[0];
    const discountIntPart = columns[1];
    const discountDecimalPart = columns[2];
    const storeColumn = columns[3];
    const isDiscountDecimalSplit =
      /^\d{8}$/.test(lm) &&
      /^\d+$/.test(discountIntPart) &&
      /^\d+$/.test(discountDecimalPart) &&
      !!storeColumn;

    if (isDiscountDecimalSplit) {
      return {
        lm,
        discountPercent: `${discountIntPart},${discountDecimalPart}`,
        store: storeColumn,
      };
    }
  }

  if (columns.length === 1 && (line.includes(';') || line.includes(','))) {
    const altSep = line.includes(';') ? ';' : ',';
    return parseClubeLeroyLine(line, altSep, storesInCsv);
  }

  return null;
}

function validateClubeLeroyDiscount(lm, discountPercent) {
  const isDiscountPercentEmpty =
    discountPercent === null || discountPercent === undefined || String(discountPercent).trim() === '';

  if (!/^\d{8}$/.test(lm)) {
    return { valid: false, errorMessage: 'price.banner.file.pattern.invalid' };
  }

  const valueRegex = /^\d{1,3}([.,]\d{1,2})?$/;
  // Corta as casas excedentes no texto para não arredondar nem perder precisão no cálculo.
  const normalizedDiscountPercent = String(discountPercent)
    .replace(',', '.')
    .replace(/^(-?\d{1,3}\.\d{2})\d+$/, '$1');
  const parsedDiscountPercent = parseFloat(normalizedDiscountPercent);
  const discountNegativeOrZero = Number.isFinite(parsedDiscountPercent) && parsedDiscountPercent <= 0;
  const discountInvalidFormat =
    !valueRegex.test(normalizedDiscountPercent) && !/^-\d{1,3}([.,]\d{1,2})?$/.test(normalizedDiscountPercent.trim());

  if (discountInvalidFormat || parsedDiscountPercent > 99.99) {
    return {
      valid: false,
      errorMessage: isDiscountPercentEmpty
        ? 'Arquivo inválido, o campo "Percentual de desconto" está vazio, realize o preenchimento no arquivo e repita a operação.'
        : 'Arquivo inválido, o campo "Percentual de desconto" está com valor acima do permitido, realize o ajuste no arquivo e repita a operação.'
    };
  }

  if (discountNegativeOrZero) {
    return {
      valid: false,
      errorMessage: isDiscountPercentEmpty
        ? 'Arquivo inválido, o campo "Percentual de desconto" está vazio, realize o preenchimento no arquivo e repita a operação.'
        : 'Arquivo inválido, o campo "Percentual de desconto" está com valor zerado ou negativo, realize o ajuste no arquivo e repita a operação.'
    };
  }

  return {
    valid: true,
    parsedDiscountPercent
  };
}

export function ListImportModal({
  open,
  onOpenChange,
  onSave,
  user,
  initialData,
  title,
  validateProducts,
  validateBranches,
  translate,
  isRetryMode = false,
  storesInCsv = false
}) {
  const [idBranches, setIdBranches] = useState([]);
  const [branchesFromFile, setBranchesFromFile] = useState([]);
  const [productBranchesByLm, setProductBranchesByLm] = useState({});
  const [productBranchExtrasByLm, setProductBranchExtrasByLm] = useState({});
  const [themeCode, setThemeCode] = useState(null);
  const [dateList, setDateList] = useState([]);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [listName, setListName] = useState('');
  const [loadingCsv, setLoadingCsv] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [isValidCsv, setIsValidCsv] = useState(null);
  const [errorOnStruct, setErrorOnStruct] = useState(false);
  const [cleanFile, setCleanFile] = useState(true);
  const [errors, setErrors] = useState({
    branches: false,
    theme: false,
    file: false,
    listName: false,
    date: false
  });
  const [validationError, setValidationError] = useState('');
  const [extractedProducts, setExtractedProducts] = useState([]);
  const [invalidProducts, setInvalidProducts] = useState([]);
  const [validProducts, setValidProducts] = useState([]);
  const [validProductsWithDescriptions, setValidProductsWithDescriptions] = useState({});
  const [showValidationResults, setShowValidationResults] = useState(false);
  const [validationComplete, setValidationComplete] = useState(false);
  const [duplicatesMessage, setDuplicatesMessage] = useState('');
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [progressInfo, setProgressInfo] = useState({ processed: 0, total: 0, originalTotal: 0, currentBatch: 0, totalBatches: 0 });
  const [retryLoading, setRetryLoading] = useState(false);
  // Evita race condition: se o usuário trocar o arquivo enquanto o parsing anterior ainda está rodando,
  // ignoramos os resultados do parsing antigo.
  const parseRequestIdRef = useRef(0);
  const prevOpenRef = useRef(false);

  const resetModalFormState = useCallback(() => {
    setIdBranches([]);
    setThemeCode(null);
    setDateList([]);
    setUploadedFile(null);
    setListName('');
    setLoadingCsv(false);
    setErrorText('');
    setIsValidCsv(null);
    setErrorOnStruct(false);
    setCleanFile(true);
    setInvalidProducts([]);
    setValidProducts([]);
    setShowValidationResults(false);
    setValidationError('');
    setValidationComplete(false);
    setProgressPercentage(0);
    setProgressInfo({ processed: 0, total: 0, originalTotal: 0, currentBatch: 0, totalBatches: 0 });
    setRetryLoading(false);
    setBranchesFromFile([]);
    setProductBranchesByLm({});
    setProductBranchExtrasByLm({});
    setExtractedProducts([]);
    setValidProductsWithDescriptions({});
    setDuplicatesMessage('');
    setErrors({
      branches: false,
      theme: false,
      file: false,
      listName: false,
      date: false
    });
    try {
      sessionStorage.removeItem(CMPM_PRODUCT_EXTRAS_STORAGE_KEY);
      sessionStorage.removeItem(CLUBE_LEROY_PRODUCT_EXTRAS_STORAGE_KEY);
    } catch (e) {
      // ignore
    }
  }, []);

  const hydrateFromInitialData = useCallback((data) => {
    if (!data) return;

    if (data.branches) {
      const branches = data.branches.map(b => ({
        value: b.idBranch,
        label: b.dsBranch
      }));
      const regionMapping = {
        "Regional Interior SP": 7,
        "Regional Centro Nordeste": 4,
        "Regional Sudeste": 1,
        "Região SP": 3,
        "Região Sul": 2
      };
      const regions = data.branchNamesGrouped
        ? data.branchNamesGrouped.map(regionName => ({
          value: regionMapping[regionName] || regionName,
          label: regionName
        }))
        : [];
      setIdBranches([...branches, ...regions]);
    } else if (data.idBranches) {
      setIdBranches(data.idBranches.map(id => ({ value: id })));
    }

    if (data.themeCode) {
      setThemeCode({
        value: data.themeCode,
        label: data.themeName || data.themeCode
      });
    }

    if (data.dtStart && data.dtEnd) {
      const start = moment(data.dtStart, ['YYYY-MM-DD', 'DD-MM-YYYY', moment.ISO_8601], true);
      const end = moment(data.dtEnd, ['YYYY-MM-DD', 'DD-MM-YYYY', moment.ISO_8601], true);
      if (start.isValid() && end.isValid()) {
        setDateList([start, end]);
      }
    }

    if (data.file) {
      setUploadedFile(data.file);
      setListName(data.file.name);
      setIsValidCsv(true);
      setCleanFile(false);
    } else if (data.listName) {
      setListName(data.listName);
    }

    if (data.idBranchesFromFile?.length) {
      setBranchesFromFile(data.idBranchesFromFile);
    }
    if (data.productBranchesByLm) {
      setProductBranchesByLm(data.productBranchesByLm);
    }
    if (data.productBranchExtrasByLm) {
      setProductBranchExtrasByLm(data.productBranchExtrasByLm);
    }

    if (data.showValidationResults) {
      setShowValidationResults(true);
      setValidationComplete(data.validationComplete !== false);
      const valid = data.validProducts || data.productLms || [];
      setValidProducts(Array.isArray(valid) ? valid : Object.keys(valid || {}));
      setExtractedProducts(data.extractedProducts || (Array.isArray(valid) ? valid : []));
      setInvalidProducts(data.invalidProducts || []);
      if (data.validProductsWithDescriptions) {
        setValidProductsWithDescriptions(data.validProductsWithDescriptions);
      }
    }
  }, []);

  // Hidrata o formulário apenas na abertura do modal. Evita que re-renders do pai
  // (ex.: após erro 409 com lastTriedPayload) resetem cleanFile/arquivo e bloqueiem novo upload.
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;

    if (!open) {
      return;
    }

    if (!wasOpen) {
      if (initialData) {
        resetModalFormState();
        hydrateFromInitialData(initialData);
      } else {
        resetModalFormState();
      }
    }
  }, [open, initialData, resetModalFormState, hydrateFromInitialData]);
  const handleThemeChange = useCallback((v) => {
    const themeChanged = themeCode?.value !== v?.value;

    setThemeCode(v);
    setErrors(e => ({ ...e, theme: false, file: false, listName: false }));

    // Se o usuário trocar o tema com um arquivo já selecionado,
    // evitar que seja submetido um arquivo validado para um tema diferente.
    if (themeChanged && uploadedFile) {
      setUploadedFile(null);
      setListName('');
      setIsValidCsv(null);
      setErrorText('');
      setErrorOnStruct(false);
      setCleanFile(true);
      setLoadingCsv(false);
      setRetryLoading(false);
      setShowValidationResults(false);
      setValidationComplete(false);
      setValidationError('');
      setExtractedProducts([]);
      setValidProducts([]);
      setInvalidProducts([]);
      setValidProductsWithDescriptions({});
      setDuplicatesMessage('');
      setProgressPercentage(0);
      setProgressInfo({ processed: 0, total: 0, originalTotal: 0, currentBatch: 0, totalBatches: 0 });
      try {
        sessionStorage.removeItem(CMPM_PRODUCT_EXTRAS_STORAGE_KEY);
        sessionStorage.removeItem(CLUBE_LEROY_PRODUCT_EXTRAS_STORAGE_KEY);
      } catch (e) {
        // ignore
      }
    }
  }, [themeCode, uploadedFile]);
  const handleBranchesChange = useCallback((e) => {
    setIdBranches(e.target.value);
    setErrors(prev => ({ ...prev, branches: false }));
  }, []);
  const handleListNameChange = useCallback((e) => {
    setListName(e.target.value);
    setErrors(e => ({ ...e, listName: false }));
  }, []);
  const handleDateChange = useCallback((v) => {
    setDateList(v);
    setErrors(e => ({ ...e, date: false }));
  }, []);
  
  const validateThemeValue = useCallback((value) => !!value?.value, []);
  
  useEffect(() => {
    if (storesInCsv && branchesFromFile.length > 0) {
      setErrors(e => ({ ...e, branches: false }));
    }
  }, [branchesFromFile, storesInCsv]);

  const validateCsv = (file) => {
    if (file.type !== 'text/csv') {
      setErrorText(translate('price.banner.csvformat'));
      return false;
    }
    const fileSize = file.size;
    const maxSize = 5 * 1024 * 1024;
    if (fileSize > maxSize) {
      setErrorText(translate('price.banner.file.size.invalid'));
      return false;
    }
    if (fileSize === 0) {
      setErrorText(translate('price.banner.file.empty'));
      return false;
    }
    return true;
  };
  const readCsv = async (file, parseRequestId) => {
    const reader = new FileReader();

    reader.onload = async ({ target }) => {
      try {
        // Se o usuário trocou o arquivo, ignoramos este parse antigo.
        if (parseRequestId !== parseRequestIdRef.current) return;

        const content = target.result;

        const lines = content.split(/\r?\n/).filter(line => line.trim() !== '');

        if (lines.length === 0) {
          setErrorText(translate('price.banner.file.empty'))
          setErrorOnStruct(true)
          setLoadingCsv(false)
          return
        }

        const separator = detectCsvSeparator(lines[0]);
        
        const isCmpm = isCmpmThemeCode(themeCode?.value);
        const isClubeLeroy = isClubeLeroyThemeCode(themeCode?.value);
        
        let codes = [];
        const collectedBranchesFromFile = [];
        const productExtrasMap = {};
        const productBranchesMap = {};
        const productBranchExtrasMap = {};
        
        if (isCmpm) {
          // CMPM format: Material, Quantidade de unidades, Percentual de desconto
          // Skip first line if it's a header
          const hasHeader = lines[0].toLowerCase().includes('material') || lines[0].toLowerCase().includes('quantidade');
          const dataLines = hasHeader ? lines.slice(1) : lines;
          
          // Detect separator from first data line.
          // When storesInCsv=true CMPM has 4 columns, so look for 4/5 first
          // using ; → \t → , order (tab before comma avoids false positives from
          // decimal commas embedded in tab-separated values like "10,625").
          const detectSeparator = (line) => {
            const quantityDecimalPointRegex = /^\d{1,7}(\.\d{1,3})?$/;

            if (storesInCsv) {
              for (const sep of [';', '\t', ',']) {
                const cols = line.split(sep).map(c => c.trim());
                if (cols.length === 4 || cols.length === 5) return sep;
              }
            }

            for (const sep of [';', '\t', ',']) {
              const cols = line.split(sep).map(c => c.trim());
              if (cols.length === 3) return sep;
              if (
                cols.length === 4 &&
                /^\d{8}$/.test(cols[0]) &&
                quantityDecimalPointRegex.test(cols[1]) &&
                /^\d+$/.test(cols[2]) &&
                /^\d{1,2}$/.test(cols[3])
              ) {
                return sep;
              }
              if (cols.length === 1 && sep === ',') {
                const inner = line.split(';').map(c => c.trim());
                if (inner.length === 3) return ';';
              }
            }
            return separator;
          };
          
          const parseCmpmLine = (line, sep) => {
            const columns = line.split(sep).map(col => col.trim());
            // Exigimos estrutura consistente com o template CMPM:
            // - 3 colunas (Material, Quantidade, Percentual de desconto)
            // - OU 4 colunas apenas quando o percentual foi "quebrado" em duas partes
            //   (ex.: 12345678;10;99;90 -> junta como 99,90).
            if (columns.length === 3) {
              const lm = columns[0];
              const quantity = columns[1];
              const discountPercent = columns[2];
              return { lm, quantity, discountPercent };
            }

            if (columns.length === 4) {
              const lm = columns[0];
              const quantity = columns[1];
              const third = columns[2];
              const fourth = columns[3];

              if (storesInCsv) {
                // In CMPM with stores, 4 columns may be: LM;quantity;discount;store
                if (/^\d{1,3}([.,]\d{1,2})?$/.test(third) && fourth) {
                  return { lm, quantity, discountPercent: third, store: fourth };
                }
                const quantityDecimalPointRegex = /^\d{1,7}(\.\d{1,3})?$/;
                const isPromoDecimalSplit =
                  /^\d{8}$/.test(lm) &&
                  quantityDecimalPointRegex.test(quantity) &&
                  /^\d+$/.test(third) &&
                  /^\d{1,2}$/.test(fourth);

                if (isPromoDecimalSplit) {
                  const discountPercent = `${third},${fourth}`;
                  return { lm, quantity, discountPercent };
                }

                return null;
              }

              const promoIntPart = third;
              const promoDecimalPart = fourth;

              // Quando o CSV está separado por ',' e o percentual foi quebrado em "99,90",
              // a quantidade costuma vir com decimal usando ponto (ex: 5.62).
              const quantityDecimalPointRegex = /^\d{1,7}(\.\d{1,3})?$/;

              const isPromoDecimalSplit =
                /^\d{8}$/.test(lm) &&
                quantityDecimalPointRegex.test(quantity) &&
                /^\d+$/.test(promoIntPart) &&
                /^\d{1,2}$/.test(promoDecimalPart);

              if (isPromoDecimalSplit) {
                const discountPercent = `${promoIntPart},${promoDecimalPart}`;
                return { lm, quantity, discountPercent };
              }
            }

            if (columns.length === 5 && storesInCsv) {
              const lm = columns[0];
              const quantity = columns[1];
              const promoIntPart = columns[2];
              const promoDecimalPart = columns[3];
              const storeColumn = columns[4];

              const quantityDecimalPointRegex = /^\d{1,7}(\.\d{1,3})?$/;
              const isPromoDecimalSplit =
                /^\d{8}$/.test(lm) &&
                quantityDecimalPointRegex.test(quantity) &&
                /^\d+$/.test(promoIntPart) &&
                /^\d{1,2}$/.test(promoDecimalPart) &&
                !!storeColumn;

              if (isPromoDecimalSplit) {
                const discountPercent = `${promoIntPart},${promoDecimalPart}`;
                return { lm, quantity, discountPercent, store: storeColumn };
              }
            }
            if (columns.length === 1 && (line.includes(';') || line.includes(','))) {
              const altSep = line.includes(';') ? ';' : ',';
              return parseCmpmLine(line, altSep);
            }
            return null;
          };
          
          let usedSeparator = separator;
          if (dataLines.length > 0) {
            usedSeparator = detectSeparator(dataLines[0]);
          }
          for (const line of dataLines) {
            // Rejeita valores em notação científica (ex.: "2,34535E+"),
            // pois fogem do padrão aceito no template CMPM.
            if (/[eE]/.test(line)) {
              setErrorText('Arquivo inválido, os campos "Quantidade de unidade" e/ou "Percentual de desconto" estão com valores acima do permitido, realize o ajuste no arquivo e repita a operação.');
              setErrorOnStruct(true);
              setLoadingCsv(false);
              return;
            }

            const parsed = parseCmpmLine(line, usedSeparator);
            if (!parsed) {
              setErrorText('Arquivo CMPM deve ter 3 colunas: Material, Quantidade de unidades, Percentual de desconto');
              setErrorOnStruct(true);
              setLoadingCsv(false);
              return;
            }
            const { lm, quantity, discountPercent, store } = parsed;
            const isQuantityEmpty = quantity === null || quantity === undefined || String(quantity).trim() === '';
            const isDiscountPercentEmpty =
              discountPercent === null || discountPercent === undefined || String(discountPercent).trim() === '';
            
            // Validate LM format (8 digits)
            if (!/^\d{8}$/.test(lm)) {
              setErrorText(translate('price.banner.file.pattern.invalid'));
              setErrorOnStruct(true);
              setLoadingCsv(false);
              return;
            }
            
            // Validate quantity (positive integer)
            const quantityRegex = /^\d{1,7}([.,]\d{1,3})?$/;
            const normalizedQuantity = String(quantity).replace(',', '.');
            const parsedQuantity = parseFloat(normalizedQuantity);
            const quantityNegativeOrZero = Number.isFinite(parsedQuantity) && parsedQuantity <= 0;
            const quantityInvalidFormat = !quantityRegex.test(quantity) && !/^-[0-9]{1,7}(\.[0-9]{1,3})?$/.test(String(quantity).trim());
            if (!Number.isFinite(parsedQuantity) || quantityInvalidFormat) {
              setErrorText(
                (isQuantityEmpty || isDiscountPercentEmpty)
                  ? 'Arquivo inválido, os campos "Quantidade de unidade" e/ou "Percentual de desconto" estão vazios, realize o preenchimento no arquivo e repita a operação.'
                  : 'Arquivo inválido, os campos "Quantidade de unidade" e/ou "Percentual de desconto" estão com valores acima do permitido, realize o ajuste no arquivo e repita a operação.'
              );
              setErrorOnStruct(true);
              setLoadingCsv(false);
              return;
            }
            if (quantityNegativeOrZero) {
              setErrorText(
                (isQuantityEmpty || isDiscountPercentEmpty)
                  ? 'Arquivo inválido, os campos "Quantidade de unidade" e/ou "Percentual de desconto" estão vazios, realize o preenchimento no arquivo e repita a operação.'
                  : 'Arquivo inválido, os campos "Quantidade de unidade" e/ou "Percentual de desconto" estão com valores zerados ou negativos, realize o ajuste no arquivo e repita a operação.'
              );
              setErrorOnStruct(true);
              setLoadingCsv(false);
              return;
            }
            
            // Validate discount percent (0.01 to 99.99 with up to 2 decimals)
            const valueRegex = /^\d{1,3}([.,]\d{1,2})?$/;
            const normalizedDiscountPercent = String(discountPercent).replace(',', '.');
            const parsedDiscountPercent = parseFloat(normalizedDiscountPercent);
            const discountNegativeOrZero = Number.isFinite(parsedDiscountPercent) && parsedDiscountPercent <= 0;
            const discountInvalidFormat = !valueRegex.test(discountPercent) && !/^-\d{1,3}([.,]\d{1,2})?$/.test(String(discountPercent).trim());
            if (discountInvalidFormat || parsedDiscountPercent > 99.99) {
              setErrorText(
                (isQuantityEmpty || isDiscountPercentEmpty)
                  ? 'Arquivo inválido, os campos "Quantidade de unidade" e/ou "Percentual de desconto" estão vazios, realize o preenchimento no arquivo e repita a operação.'
                  : 'Arquivo inválido, os campos "Quantidade de unidade" e/ou "Percentual de desconto" estão com valores acima do permitido, realize o ajuste no arquivo e repita a operação.'
              );
              setErrorOnStruct(true);
              setLoadingCsv(false);
              return;
            }
            if (discountNegativeOrZero) {
              setErrorText(
                (isQuantityEmpty || isDiscountPercentEmpty)
                  ? 'Arquivo inválido, os campos "Quantidade de unidade" e/ou "Percentual de desconto" estão vazios, realize o preenchimento no arquivo e repita a operação.'
                  : 'Arquivo inválido, os campos "Quantidade de unidade" e/ou "Percentual de desconto" estão com valores zerados ou negativos, realize o ajuste no arquivo e repita a operação.'
              );
              setErrorOnStruct(true);
              setLoadingCsv(false);
              return;
            }
            
            codes.push(lm);
            // Se as lojas estiverem em formato CSV, tente extraí-las da mesma linha
            if (storesInCsv) {
              const storeValue = store || splitCsvLine(line, usedSeparator).slice(-1)[0] || '';
              if (!storeValue || String(storeValue).trim() === '') {
                setErrorText(translate(PRICEBANNER_INVALID_STORES_I18N_KEY));
                setErrorOnStruct(true);
                setLoadingCsv(false);
                return;
              }
              const normalizedStore = String(storeValue).trim();
              collectedBranchesFromFile.push(normalizedStore);
              productBranchesMap[lm] = [...(productBranchesMap[lm] || []), normalizedStore];
              productBranchExtrasMap[lm] = [...(productBranchExtrasMap[lm] || []), {
                idBranch: normalizedStore,
                quantity: normalizedQuantity,
                discountPercent: parsedDiscountPercent
              }];
            }
            productExtrasMap[lm] = {
              // BigDecimal do backend aceita string/number; usamos string normalizada para preservar casas decimais.
              quantity: normalizedQuantity,
              discountPercent: parsedDiscountPercent
            };
          }
        } else if (isClubeLeroy) {
          const hasHeader = lines[0].toLowerCase().includes('material') || lines[0].toLowerCase().includes('desconto');
          const dataLines = hasHeader ? lines.slice(1) : lines;
          let usedSeparator = separator;
          if (dataLines.length > 0) {
            usedSeparator = detectClubeLeroySeparator(dataLines[0], storesInCsv, separator);
          }

          for (const line of dataLines) {
            if (/[eE]/.test(line)) {
              setErrorText('Arquivo inválido, o campo "Percentual de desconto" está com valor acima do permitido, realize o ajuste no arquivo e repita a operação.');
              setErrorOnStruct(true);
              setLoadingCsv(false);
              return;
            }

            const parsed = parseClubeLeroyLine(line, usedSeparator, storesInCsv);
            if (!parsed) {
              setErrorText(
                storesInCsv
                  ? 'Arquivo Clube Leroy deve ter 3 colunas: Material, Percentual de desconto e Lojas'
                  : 'Arquivo Clube Leroy deve ter 2 colunas: Material, Percentual de desconto'
              );
              setErrorOnStruct(true);
              setLoadingCsv(false);
              return;
            }

            const { lm, discountPercent, store } = parsed;
            const validation = validateClubeLeroyDiscount(lm, discountPercent);
            if (!validation.valid) {
              setErrorText(
                validation.errorMessage === 'price.banner.file.pattern.invalid'
                  ? translate('price.banner.file.pattern.invalid')
                  : validation.errorMessage
              );
              setErrorOnStruct(true);
              setLoadingCsv(false);
              return;
            }

            codes.push(lm);
            if (storesInCsv) {
              const storeValue = store || splitCsvLine(line, usedSeparator).slice(-1)[0] || '';
              if (!storeValue || String(storeValue).trim() === '') {
                setErrorText(translate(PRICEBANNER_INVALID_STORES_I18N_KEY));
                setErrorOnStruct(true);
                setLoadingCsv(false);
                return;
              }
              const normalizedStore = String(storeValue).trim();
              collectedBranchesFromFile.push(normalizedStore);
              productBranchesMap[lm] = [...(productBranchesMap[lm] || []), normalizedStore];
              productBranchExtrasMap[lm] = [...(productBranchExtrasMap[lm] || []), {
                idBranch: normalizedStore,
                discountPercent: validation.parsedDiscountPercent
              }];
            }
            productExtrasMap[lm] = {
              discountPercent: validation.parsedDiscountPercent
            };
          }
        } else {
          // Formato padrão: coluna única com códigos LM
          // Se storesInCsv=true, espere um cabeçalho contendo a coluna 'Lojas' e o produto LM na primeira coluna.
          let lojasIndex = -1;
          const firstLineCols = splitCsvLine(lines[0], separator);
          const headerLower = firstLineCols.map(normalizeCsvHeader);
          const hasHeader = storesInCsv && headerLower.some(isStoreColumnHeader);

          if (storesInCsv && hasHeader) {
            lojasIndex = headerLower.findIndex(isStoreColumnHeader);
            // Índice de colunas LM: tente 'material', 'lm' ou use a primeira coluna.
            let lmIndex = headerLower.findIndex(h => h === 'material' || h === 'lm');
            if (lmIndex < 0) {
              lmIndex = 0;
            }
            const dataLines = lines.slice(1);
            codes = [];
            for (const line of dataLines) {
              const cols = splitCsvLine(line, separator);
              const lm = (cols[lmIndex] || '').trim();
              const lojaVal = (cols[lojasIndex] || '').trim();
              if (!lm || !/^\d{8}$/.test(lm)) {
                setErrorText(translate('price.banner.file.pattern.invalid'));
                setErrorOnStruct(true);
                setLoadingCsv(false);
                return;
              }
              if (!lojaVal) {
                setErrorText(translate(PRICEBANNER_INVALID_STORES_I18N_KEY));
                setErrorOnStruct(true);
                setLoadingCsv(false);
                return;
              }
              codes.push(lm);
              const normalizedStore = lojaVal;
              collectedBranchesFromFile.push(normalizedStore);
              productBranchesMap[lm] = [...(productBranchesMap[lm] || []), normalizedStore];
            }
          } else if (storesInCsv && !hasHeader) {
            // Sem cabeçalho: LM na primeira coluna e loja na última.
            codes = [];
            let dataLinesNoHeader = lines;
            const firstColsLower = splitCsvLine(lines[0], separator).map(normalizeCsvHeader);
            const firstLineIsHeader =
              firstColsLower.some(isStoreColumnHeader) ||
              firstColsLower.some(h => h === 'lm' || h === 'material');
            if (firstLineIsHeader) {
              dataLinesNoHeader = lines.slice(1);
            }
            for (const line of dataLinesNoHeader) {
              const cols = splitCsvLine(line, separator);
              const lm = (cols[0] || '').trim();
              const lojaVal = (cols[cols.length - 1] || '').trim();
              if (!lm || !/^\d{8}$/.test(lm)) {
                setErrorText(translate('price.banner.file.pattern.invalid'));
                setErrorOnStruct(true);
                setLoadingCsv(false);
                return;
              }
              if (!lojaVal) {
                setErrorText(translate(PRICEBANNER_INVALID_STORES_I18N_KEY));
                setErrorOnStruct(true);
                setLoadingCsv(false);
                return;
              }
              codes.push(lm);
              const normalizedStore = lojaVal;
              collectedBranchesFromFile.push(normalizedStore);
              productBranchesMap[lm] = [...(productBranchesMap[lm] || []), normalizedStore];
            }
          } else {
            codes = lines.map(line => line.split(separator)[0].trim());
          }
          const isValid = codes.every(code => /^\d{8}$/.test(code));
          
          // Validação de estrutura ajustada para considerar se há lojas no CSV
          let isValidStructure = true;
          if (storesInCsv && hasHeader) {
            // Com cabeçalho e lojas: valida linhas de dados (após cabeçalho)
            // Cada linha de dados deve ter: LM (coluna 0) + loja (coluna lojasIndex) preenchidas
            // Pode haver mais de uma coluna, então não validamos estrutura rígida
            isValidStructure = codes.every(code => /^\d{8}$/.test(code));
          } else if (storesInCsv && !hasHeader) {
            // Sem cabeçalho mas com lojas: mesma coisa
            isValidStructure = codes.every(code => /^\d{8}$/.test(code));
          } else {
            // Sem lojas: estrutura padrão - apenas 1 coluna com dados
            isValidStructure = lines.every(line => {
              const columns = line.split(separator).map(col => col.trim());
              return columns.length >= 1 && columns.slice(1).every(col => col === '' || col.length === 0);
            });
          }
          
          if (!isValid) {
            setErrorText(translate('price.banner.file.pattern.invalid'));
            setErrorOnStruct(true);
            setLoadingCsv(false);
            return;
          }
          if (!isValidStructure) {
            setErrorText('Arquivo com estrutura inválida. Use apenas códigos de 8 dígitos na primeira coluna.');
            setErrorOnStruct(true);
            setLoadingCsv(false);
            return;
          }
        }
        
        const uniqueCodes = [...new Set(codes)];
        const duplicatesRemoved = countCsvProductDuplicatesRemoved(
          codes,
          collectedBranchesFromFile,
          storesInCsv
        );

        setExtractedProducts(uniqueCodes);

        if (storesInCsv && collectedBranchesFromFile.length > 0) {
          setBranchesFromFile([...new Set(collectedBranchesFromFile)]);
          setErrors(e => ({ ...e, branches: false }));
        } else if (storesInCsv) {
          setBranchesFromFile([]);
        }
        setProductBranchesByLm(productBranchesMap);
        setProductBranchExtrasByLm(productBranchExtrasMap);
        
        const productExtrasStorageKey = getProductExtrasStorageKeyByTheme(themeCode?.value);
        if (productExtrasStorageKey && Object.keys(productExtrasMap).length > 0) {
          sessionStorage.setItem(productExtrasStorageKey, JSON.stringify(productExtrasMap));
          [CMPM_PRODUCT_EXTRAS_STORAGE_KEY, CLUBE_LEROY_PRODUCT_EXTRAS_STORAGE_KEY]
            .filter(key => key !== productExtrasStorageKey)
            .forEach(key => sessionStorage.removeItem(key));
        } else {
          sessionStorage.removeItem(CMPM_PRODUCT_EXTRAS_STORAGE_KEY);
          sessionStorage.removeItem(CLUBE_LEROY_PRODUCT_EXTRAS_STORAGE_KEY);
        }
        
        setLoadingCsv(true);

        if (storesInCsv) {
          const branchesValid = await validateStoresFromCsv(collectedBranchesFromFile, parseRequestId);
          if (!branchesValid) {
            return;
          }
        }

        if (uniqueCodes.length > 0) {
          await validateProductsOnUpload(uniqueCodes, duplicatesRemoved, codes.length);
        } else {
          setLoadingCsv(false);
        }

      } catch (error) {
        setErrorText('Erro ao processar o arquivo');
        setErrorOnStruct(true);
        setLoadingCsv(false);
      }
    };

    reader.readAsText(file);
  };

  const failInvalidStoresValidation = () => {
    setErrorText(translate(PRICEBANNER_INVALID_STORES_I18N_KEY));
    setErrorOnStruct(true);
    setLoadingCsv(false);
    setShowValidationResults(false);
    setValidationComplete(false);
    setValidationError('');
    setExtractedProducts([]);
    setValidProducts([]);
    setInvalidProducts([]);
    setValidProductsWithDescriptions({});
    setDuplicatesMessage('');
    setProgressPercentage(0);
    setProgressInfo({ processed: 0, total: 0, originalTotal: 0, currentBatch: 0, totalBatches: 0 });
  };

  const validateStoresFromCsv = async (branchCodes, parseRequestId) => {
    if (parseRequestId !== parseRequestIdRef.current) {
      return false;
    }

    if (!branchCodes || branchCodes.length === 0) {
      failInvalidStoresValidation();
      return false;
    }

    try {
      const result = await validateBranches(branchCodes);
      if (parseRequestId !== parseRequestIdRef.current) {
        return false;
      }

      const invalidCount = Object.keys(result?.invalidBranches || {}).length;
      if (invalidCount > 0) {
        failInvalidStoresValidation();
        return false;
      }

      return true;
    } catch (error) {
      if (parseRequestId !== parseRequestIdRef.current) {
        return false;
      }
      failInvalidStoresValidation();
      return false;
    }
  };

  const validateProductsOnUpload = async (products, duplicatesRemoved = 0, originalTotal = 0) => {
    if (!products || products.length === 0) {
      setValidationComplete(true);
      setTimeout(() => {
        setLoadingCsv(false);
      }, 1500);
      return;
    }

    setProgressPercentage(0);
    setProgressInfo({ processed: 0, total: products.length, originalTotal: products.length, currentBatch: 0, totalBatches: 0 });

    try {
      const handleProgress = (progressData) => {
        setProgressPercentage(progressData.percentage);
        setProgressInfo({
          processed: progressData.processed,
          total: progressData.total,
          originalTotal: originalTotal || products.length,
          currentBatch: progressData.currentBatch,
          totalBatches: progressData.totalBatches
        });
      };

      const response = await validateProducts(products, handleProgress);
      const valid = response?.validProducts ? Object.keys(response.validProducts) : [];
      const invalid = response?.invalidProducts ? Object.keys(response.invalidProducts) : [];
      const productsWithDescriptions = response?.validProducts || {};
      setValidProductsWithDescriptions(productsWithDescriptions);
      setValidProducts(valid);
      setInvalidProducts(invalid);
      setShowValidationResults(true);
      setValidationComplete(true);
      
      setProgressPercentage(100);
      
      if (duplicatesRemoved > 0 && !storesInCsv) {
        const duplicateMessage = duplicatesRemoved === 1
          ? `${duplicatesRemoved} LM duplicado foi removido automaticamente.`
          : `${duplicatesRemoved} LMs duplicados foram removidos automaticamente.`;
        setDuplicatesMessage(duplicateMessage);
      } else {
        setDuplicatesMessage('');
      }
      
      setTimeout(() => {
        setLoadingCsv(false);
        setProgressPercentage(0);
        setProgressInfo({ processed: 0, total: 0, currentBatch: 0, totalBatches: 0 });
      }, 1000);
    } catch (error) {
      setValidationError(translate('price.banner.error.validation.products'));
      setShowValidationResults(true);
      setValidationComplete(true);
      setProgressPercentage(100);
      setTimeout(() => {
        setLoadingCsv(false);
        setProgressPercentage(0);
        setProgressInfo({ processed: 0, total: 0, currentBatch: 0, totalBatches: 0 });
      }, 1000);
    }
  };

  const handleRetryFailedProducts = async () => {
    if (!invalidProducts || invalidProducts.length === 0) {
      return;
    }

    setRetryLoading(true);
    setValidationError('');
    setLoadingCsv(true);
    setValidationComplete(false);
    setProgressPercentage(0);
    setProgressInfo({ processed: 0, total: invalidProducts.length, originalTotal: invalidProducts.length, currentBatch: 0, totalBatches: 0 });

    try {
      const handleProgress = (progressData) => {
        setProgressPercentage(progressData.percentage);
        setProgressInfo({
          processed: progressData.processed,
          total: progressData.total,
          originalTotal: invalidProducts.length,
          currentBatch: progressData.currentBatch,
          totalBatches: progressData.totalBatches
        });
      };

      const response = await validateProducts(invalidProducts, handleProgress);
      const valid = response?.validProducts ? Object.keys(response.validProducts) : [];
      const invalid = response?.invalidProducts ? Object.keys(response.invalidProducts) : [];
      const productsWithDescriptions = response?.validProducts || {};
      
      const newValidProducts = [...validProducts, ...valid];
      const newValidProductsWithDescriptions = { ...validProductsWithDescriptions, ...productsWithDescriptions };
      
      setValidProductsWithDescriptions(newValidProductsWithDescriptions);
      setValidProducts(newValidProducts);
      setInvalidProducts(invalid);
      setShowValidationResults(true);
      setValidationComplete(true);
      setProgressPercentage(100);
      
      setTimeout(() => {
        setRetryLoading(false);
        setLoadingCsv(false);
        setProgressPercentage(0);
        setProgressInfo({ processed: 0, total: 0, originalTotal: 0, currentBatch: 0, totalBatches: 0 });
      }, 1000);
    } catch (error) {
      setValidationError(translate('price.banner.error.validation.products'));
      setShowValidationResults(true);
      setValidationComplete(true);
      setProgressPercentage(100);
      setTimeout(() => {
        setRetryLoading(false);
        setLoadingCsv(false);
        setProgressPercentage(0);
        setProgressInfo({ processed: 0, total: 0, originalTotal: 0, currentBatch: 0, totalBatches: 0 });
      }, 1000);
    }
  };

  const handleConfirm = async () => {
    const newErr = {
      branches: storesInCsv
        ? !branchesFromFile || branchesFromFile.length === 0
        : idBranches.length === 0,
      theme: !themeCode || !themeCode.value,
      date: !dateList || dateList.length !== 2,
      file: !uploadedFile,
      listName: !listName || listName.trim() === ''
    };
    const hasError = newErr.branches || newErr.theme || newErr.date || newErr.file || newErr.listName;
    if (!user?.username || !user?.fullName) {
      setErrorText('Erro: Dados do usuário não estão disponíveis. Por favor, faça login novamente.');
      setErrorOnStruct(true);
      return;
    }
    if (hasError) {
      setErrors(newErr);
      return;
    }
    if (showValidationResults && validProducts.length === 0) {
      return;
    }
    const finalProductLms = showValidationResults && validProducts.length > 0 ? validProducts : extractedProducts;
    const finalProductDescriptions = showValidationResults && Object.keys(validProductsWithDescriptions).length > 0
      ? validProductsWithDescriptions
      : {};
    
    const productExtrasStorageKey = getProductExtrasStorageKeyByTheme(themeCode?.value);
    const storedExtras = productExtrasStorageKey ? sessionStorage.getItem(productExtrasStorageKey) : null;
    const productExtrasRaw = storedExtras ? JSON.parse(storedExtras) : {};
    
    // Filter extras to include only valid products
    const productExtrasByLm = {};
    finalProductLms.forEach(lm => {
      if (productExtrasRaw[lm]) {
        productExtrasByLm[lm] = productExtrasRaw[lm];
      }
    });
    
    const metadata = {
      idBranches: idBranches
        .flatMap(b => {
          if (b.value && !b.idBranches) {
            return [b.value];
          }
          return b.idBranches || [];
        })
        .filter(id => typeof id === 'string' && id !== 'all'),
      name: listName,
      themeCode: themeCode?.value || null, 
      themeName: themeCode?.label || '',
      dtStart: dateList[0]?.format('YYYY-MM-DD'),
      dtEnd: dateList[1]?.format('YYYY-MM-DD'),
      cdLdap: user?.username || '',
      dsLdap: user?.fullName || '',
      productLms: finalProductLms,
      productDescriptions: finalProductDescriptions,
      ...(Object.keys(productExtrasByLm).length > 0 && { productExtrasByLm }),
      ...(storesInCsv && Object.keys(productBranchesByLm).length > 0 && { productBranchesByLm }),
      ...(storesInCsv && Object.keys(productBranchExtrasByLm).length > 0 && { productBranchExtrasByLm }),
      ...(storesInCsv && branchesFromFile && branchesFromFile.length > 0 && { idBranchesFromFile: branchesFromFile })
    };

    if (!user?.username || !user?.fullName) {
      setErrorText(translate('application.fatal.error'));
      setErrorOnStruct(true);
      return;
    }
    if (!dateList[0] || !dateList[1]) {
      return;
    }

    onSave && onSave(metadata, uploadedFile);
  };
  const handleFileUpload = (files) => {
    setErrorOnStruct(false);
    setValidationError('');
    setShowValidationResults(false);
    setValidationComplete(false);
    setValidProducts([]);
    setInvalidProducts([]);
    setValidProductsWithDescriptions({});
    setExtractedProducts([]);
    try {
      sessionStorage.removeItem(CMPM_PRODUCT_EXTRAS_STORAGE_KEY);
      sessionStorage.removeItem(CLUBE_LEROY_PRODUCT_EXTRAS_STORAGE_KEY);
    } catch (e) {
      // ignore
    }
    const file = files[0];
    if (validateCsv(file)) {
      setUploadedFile(file);
      setListName(file.name);
      setIsValidCsv(true);
      setCleanFile(false);
      // Bloqueia "Importar" enquanto o CSV está sendo lido/validado.
      // Isso evita submissão com estado residual ou antes do parsing terminar.
      setLoadingCsv(true);
      setProgressPercentage(0);
      setProgressInfo({
        processed: 0,
        total: 0,
        originalTotal: 0,
        currentBatch: 0,
        totalBatches: 0
      });

      // Gera novo id de parse para invalidar resultados anteriores.
      parseRequestIdRef.current += 1;
      const currentParseRequestId = parseRequestIdRef.current;
      setBranchesFromFile([]);
      readCsv(file, currentParseRequestId);
      setErrors(e => ({ ...e, file: false, listName: false, branches: false }));
      setProductBranchesByLm({});
      setProductBranchExtrasByLm({});
    } else {
      setIsValidCsv(false);
      setErrors(e => ({ ...e, file: true }));
    }
  };

  const handleRemoveUploadedFile = () => {
    // Invalida parse em andamento, caso exista.
    parseRequestIdRef.current += 1;

    setUploadedFile(null);
    setCleanFile(true);
    setListName('');
    setIsValidCsv(null);
    setErrorText('');
    setErrorOnStruct(false);
    setLoadingCsv(false);
    setRetryLoading(false);
    setShowValidationResults(false);
    setValidationComplete(false);
    setValidationError('');
    setExtractedProducts([]);
    setInvalidProducts([]);
    setValidProducts([]);
    setValidProductsWithDescriptions({});
    setProductBranchesByLm({});
    setProductBranchExtrasByLm({});
    setBranchesFromFile([]);
    try {
      sessionStorage.removeItem(CMPM_PRODUCT_EXTRAS_STORAGE_KEY);
      sessionStorage.removeItem(CLUBE_LEROY_PRODUCT_EXTRAS_STORAGE_KEY);
    } catch (e) {
      // ignore
    }

    setErrors(e => ({ ...e, file: false, listName: false, branches: false }));
  };
  const handleDownloadTemplate = () => {
    if (!themeCode || !themeCode.value) return
    const isCmpm = isCmpmThemeCode(themeCode.value);
    const isClubeLeroy = isClubeLeroyThemeCode(themeCode.value);
    
    let model;
    let filename;
    
    if (isCmpm) {
      // CMPM template: use ";" so Excel (pt-BR) opens as 3 separate columns
      if (storesInCsv) {
        model = 'Material;Quantidade de unidades;Percentual de desconto;Lojas\n' +
                '89615435;10,625;99,90;0001\n' +
                '89337283;5,620;10,50;0002\n' +
                '12345678;20;0,00;0003';
        filename = 'model_list_theme_cmpm_with_stores.csv';
      } else {
        model = 'Material;Quantidade de unidades;Percentual de desconto\n' +
                '89615435;10,625;99,90\n' +
                '89337283;5,620;10,50\n' +
                '12345678;20;0,00';
        filename = 'model_list_theme_cmpm.csv';
      }
    } else if (isClubeLeroy) {
      if (storesInCsv) {
        model = 'Material;Percentual de desconto;Lojas\n' +
                '89615435;15,00;0001\n' +
                '89337283;10,50;0002\n' +
                '12345678;20;0003';
        filename = 'model_list_theme_clube_leroy_with_stores.csv';
      } else {
        model = 'Material;Percentual de desconto\n' +
                '89615435;15,00\n' +
                '89337283;10,50\n' +
                '12345678;20';
        filename = 'model_list_theme_clube_leroy.csv';
      }
    } else {
      // Standard template with single column
      if (storesInCsv) {
        model = 'LM;Lojas\n' +
                '89615435;0001\n' +
                '89337283;0002\n' +
                '12345678;0003\n';
        filename = 'model_list_theme_with_stores.csv';
      } else {
        model = [
          '89615435',
          '89337283',
          '12345678',
          '87654321'
        ].join('\n');
        filename = 'model_list_theme.csv';
      }
    }

    const csvData = new Blob([model], { type: 'text/csv;charset=utf-8;' });
    let csvURL = null;
    if (navigator.msSaveBlob) {
      csvURL = navigator.msSaveBlob(csvData, filename);
    } else {
      csvURL = window.URL.createObjectURL(csvData);
    }

    const tempLink = document.createElement('a');
    tempLink.href = csvURL;
    tempLink.setAttribute('download', filename);
    tempLink.click();
  };

  const handleCloseModal = () => {
    onOpenChange(false);
  };

  if (!open) return null;

  return (
    <>
      <style>
        {`
          @keyframes loading-shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
        `}
      </style>
      <Modal
      title={title}
      size="md"
      isOpen={open}
      onCloseModal={handleCloseModal}
      useControlledState
      className="price-banner-upload-modal"
      showFooter={true}
      hideCancelButton={false}
      cancelButtonText={translate('price.banner.downloadtemplate')}
      confirmButtonText={translate('price.banner.import')}
      onConfirm={handleConfirm}
      onCancelButtonClick={handleDownloadTemplate}
      isCancelButtonDisabled={!(themeCode && themeCode.value)}
      isConfirmButtonDisabled={Object.values(errors).some(error => error) || loadingCsv || retryLoading || errorOnStruct || (!isRetryMode && !isValidCsv) || (!showValidationResults && isValidCsv && !initialData && !isRetryMode) || (showValidationResults && validProducts.length === 0 && invalidProducts.length > 0 && !retryLoading)}
      noCloseOnConfirm
    >
      <div className="w-100">
          {!storesInCsv && (
          <div className="w-100">
            <RegionBranchMultipleSelect
              required
              name="idBranches"
              label={translate('price.banner.stores')}
              placeholder={translate('field.selectMultiStores')}
              value={idBranches}
              onChange={handleBranchesChange}
              validateValue={value => !!value && value.length > 0}
              errorText={errors.branches ? translate('mandatory') : undefined}
              showHelperArea={false}
              selectAll
              isMulti
              user={user}
              translate={translate}
              withoutRegions
              excludeInactive={true}
              attrs={{
                fill: true
              }}
            />
          </div>
        )}
        <div className="w-100">
          <ThemeSelectField
            label={translate('price.banner.theme')}
            placeholder="Selecione um tema"
            value={themeCode}
            onChange={handleThemeChange}
            required
            errorText={errors.theme ? translate('mandatory') : undefined}
            showHelperArea={false}
            validateValue={validateThemeValue}
            user={user}
            // Evita burlar a validação: após selecionar um arquivo, o tema deve permanecer fixo
            // (o usuário deve trocar o arquivo para tentar outro tema).
            disabled={!!uploadedFile}
            fill
            translate={translate}
            disableLoading={loadingCsv}
          />
        </div>
        <div className="w-100 custom-height-input">
          {loadingCsv && (
            <div className={`loading-status margin-bottom ${validationComplete ? 'complete' : 'loading'}`} style={{ '--default-bar-width': '0%' }}>
              <span style={{ 
                fontWeight: '600',
                color: validationComplete ? '#fff' : '#dcf5dc'
              }}>
                {validationComplete
                  ? 'Carregamento completo'
                  : `${translate('loading')} ${progressInfo.processed} / ${progressInfo.originalTotal}`
                }
              </span>
              <div 
                className={`bar ${!validationComplete ? 'loading' : 'complete'}`}
                style={{ 
                  width: `${validationComplete ? 100 : Math.max(progressPercentage, 2)}%`,
                  transition: validationComplete ? 'width 0.3s ease-in-out' : 'width 0.2s ease-out',
                  background: validationComplete 
                    ? '#41a017' 
                    : 'linear-gradient(90deg, #41a017 0%, #52b91f 50%, #41a017 100%)',
                  backgroundSize: validationComplete ? '100% 100%' : '200% 100%',
                  animation: validationComplete ? 'none' : 'loading-shimmer 2s infinite'
                }} 
              />
            </div>
          )}
          <FileInput
            cleanFile={cleanFile}
            resetValue
            required
            errorOnStruct={errorOnStruct}
            successText={loadingCsv ? 'Carregando arquivo...' : 'Arquivo carregado com sucesso!'}
            validateValue={(f) => f[0].type === 'text/csv' && f[0].size <= 5 * 1024 * 1024 && f[0].size > 0}
            errorText={errorText}
            disabled={!(themeCode && themeCode.value) || loadingCsv}
            placeholder="Escolha um arquivo"
            hideFileInput={loadingCsv}
            className="custom-height-input"
            helpText={translate('price.banner.file.help')}
            accept={'.csv'}
            onChange={handleFileUpload}
            showClearFile={!!uploadedFile}
            onClearFile={handleRemoveUploadedFile}
            label={translate('price.banner.attachfile')}
            name="file"
          />
          {storesInCsv && errors.branches && branchesFromFile.length === 0 && !loadingCsv && (
            <div className="w-100" style={{ color: '#c62828', marginTop: '8px', fontSize: '14px' }}>
              {translate(PRICEBANNER_INVALID_STORES_I18N_KEY)}
            </div>
          )}
        </div>
        {showValidationResults && validProducts.length > 0 && invalidProducts.length > 0 && !loadingCsv && (
          <div className="w-100">
            <SupplyItemsNotFoundBox
              topMsg="Os seguintes produtos não foram encontrados e não serão considerados quando a lista for salva:"
              bottomMsg={duplicatesMessage}
              items={invalidProducts}
              showRetryButton={true}
              onRetry={handleRetryFailedProducts}
              retryLoading={retryLoading}
              translate={translate}
            />
          </div>
        )}
        {showValidationResults && validProducts.length === 0 && invalidProducts.length > 0 && (
          <div className="w-100">
            <SupplyItemsNotFoundBox
              error
              topMsg="Nenhum produto foi encontrado. Ajuste os produtos do arquivo e refaça o upload."
              bottomMsg={duplicatesMessage}
              items={invalidProducts}
            />
          </div>
        )}
        {showValidationResults && validProducts.length > 0 && invalidProducts.length === 0 && duplicatesMessage && (
          <div className="w-100">
            <SupplyItemsNotFoundBox
              topMsg="Produtos validados com sucesso!"
              bottomMsg={duplicatesMessage}
              items={[]}
            />
          </div>
        )}
        {validationError && (
          <div className="w-100">
            <SupplyItemsNotFoundBox
              error
              topMsg={translate('price.banner.error.validation.products')}
              bottomMsg=""
              items={[]}
            />
          </div>
        )}
        <div className="w-100 custom-height-input">
          <TextField
            required
            readOnly={false}
            disabled={false}
            label={translate('price.banner.listname')}
            placeholder={translate('irc.choose-name')}
            name="listName"
            fill
            onChange={handleListNameChange}
            translate={translate}
            value={listName}
            validateValue={(v) => Boolean(v)}
            errorText={errors.listName ? translate('mandatory') : undefined}
          />
        </div>
        <div className="w-100 custom-height-input">
          <DateIntervalInput
            label={translate('price.banner.period')}
            initialValue={dateList}
            onChange={handleDateChange}
            required
            validateValue={value => Array.isArray(value) && value.length === 2 && !!value[0] && !!value[1]}
            error={errors.date ? 'error' : undefined}
            errorMsg={errors.date ? translate('mandatory') : undefined}
            selectAll
            isMulti
            noSelect
            minDate={new Date()}
            disabled={false}
            useMinDate
            disableAutoClose
            user={user}
          />
        </div>
      </div>
    </Modal>
    </>
  );
}
ListImportModal.propTypes = {
  title: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  user: PropTypes.object,
  initialData: PropTypes.object,
  validateProducts: PropTypes.func.isRequired,
  validateBranches: PropTypes.func.isRequired,
  translate: PropTypes.func.isRequired,
  isRetryMode: PropTypes.bool,
  storesInCsv: PropTypes.bool
};
const mapStateToProps = () => ({});
const mapDispatchToProps = (dispatch) => bindActionCreators({
  validateProducts: pricebannerListActions.validateProducts,
  validateBranches: pricebannerListActions.validateBranches
}, dispatch);
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(translate(null, { wait: true, translateFuncName: 'translate' })(ListImportModal));
